import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation, Link } from "wouter";
import { apiRequest, queryClient } from "../lib/api";
import { ArrowLeft, Save, Globe, Trash2 } from "lucide-react";

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  cover_image: string | null;
  category: string;
  type: string;
  tags: string[] | null;
  author: string;
  meta_title: string | null;
  meta_description: string | null;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-zа-яё0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function BlogEditorPage() {
  const [, paramsById] = useRoute("/blog/:id");
  const isNew = !paramsById?.id || paramsById.id === "new";
  const postId = isNew ? null : paramsById?.id;
  const [, navigate] = useLocation();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [category, setCategory] = useState("ai");
  const [type, setType] = useState("article");
  const [tagsInput, setTagsInput] = useState("");
  const [author, setAuthor] = useState("GPT Manager");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [content, setContent] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const { data: existingPost, isLoading } = useQuery<BlogPost>({
    queryKey: [`/api/admin/blog/${postId}`],
    enabled: !!postId,
  });

  useEffect(() => {
    if (existingPost) {
      setTitle(existingPost.title);
      setSlug(existingPost.slug);
      setExcerpt(existingPost.excerpt || "");
      setCategory(existingPost.category);
      setType(existingPost.type || "article");
      setTagsInput(existingPost.tags?.join(", ") || "");
      setAuthor(existingPost.author);
      setMetaTitle(existingPost.meta_title || "");
      setMetaDescription(existingPost.meta_description || "");
      setCoverImage(existingPost.cover_image || "");
      setContent(existingPost.content);
      setSlugManuallyEdited(true);
    }
  }, [existingPost]);

  useEffect(() => {
    if (!slugManuallyEdited && title) {
      setSlug(generateSlug(title));
    }
  }, [title, slugManuallyEdited]);

  function buildBody(isPublished: boolean) {
    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    return {
      title,
      slug,
      excerpt: excerpt || null,
      content,
      coverImage: coverImage || null,
      category,
      type,
      tags: tags.length > 0 ? tags : null,
      author,
      metaTitle: metaTitle || null,
      metaDescription: metaDescription || null,
      isPublished,
      publishedAt: isPublished ? new Date().toISOString() : (existingPost?.published_at || null),
    };
  }

  const saveMutation = useMutation({
    mutationFn: async (isPublished: boolean) => {
      const body = buildBody(isPublished);
      if (postId) {
        await apiRequest("PUT", `/api/admin/blog/${postId}`, body);
      } else {
        await apiRequest("POST", `/api/admin/blog`, body);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/blog"] });
      if (postId) {
        queryClient.invalidateQueries({ queryKey: [`/api/admin/blog/${postId}`] });
      }
      navigate("/blog");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/admin/blog/${postId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/blog"] });
      navigate("/blog");
    },
  });

  function handleDelete() {
    if (window.confirm("Удалить этот пост? Это действие нельзя отменить.")) {
      deleteMutation.mutate();
    }
  }

  if (!isNew && isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded w-64 animate-pulse" />
        <div className="h-64 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  if (!isNew && !existingPost && !isLoading) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        Пост не найден
      </div>
    );
  }

  const isSaving = saveMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link href="/blog">
            <button className="p-1.5 rounded hover:bg-muted transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
          </Link>
          <h1 className="text-2xl font-bold">
            {isNew ? "Новый пост" : "Редактирование поста"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="px-4 py-2 border border-destructive text-destructive rounded-md text-sm font-medium hover:bg-destructive/10 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {isDeleting ? "..." : "Удалить"}
            </button>
          )}
          <button
            onClick={() => saveMutation.mutate(false)}
            disabled={isSaving || !title.trim() || !content.trim()}
            className="px-4 py-2 border border-input rounded-md text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {isSaving ? "..." : "Сохранить черновик"}
          </button>
          <button
            onClick={() => saveMutation.mutate(true)}
            disabled={isSaving || !title.trim() || !content.trim()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Globe className="w-4 h-4" />
            {isSaving ? "..." : "Опубликовать"}
          </button>
        </div>
      </div>

      {/* Error */}
      {saveMutation.error && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          {(saveMutation.error as Error).message}
        </div>
      )}
      {deleteMutation.error && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          {(deleteMutation.error as Error).message}
        </div>
      )}

      {/* Form */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content Column */}
        <div className="lg:col-span-2 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Заголовок <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Заголовок поста"
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugManuallyEdited(true);
              }}
              placeholder="url-slug"
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring font-mono"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Краткое описание</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              rows={2}
              placeholder="Краткое описание поста для превью..."
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Контент (Markdown) <span className="text-destructive">*</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={20}
              placeholder="Содержимое поста в формате Markdown..."
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y font-mono"
            />
          </div>
        </div>

        {/* Sidebar Column */}
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-card p-4 space-y-4">
            <h3 className="font-semibold text-sm">Настройки</h3>

            <div>
              <label className="block text-sm font-medium mb-1">Тип</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
              >
                <option value="article">Статья (блог)</option>
                <option value="case">Кейс</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Кейсы показываются на странице «Кейсы», статьи — в блоге
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Категория</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
              >
                <option value="ai">ИИ</option>
                <option value="sales">Продажи</option>
                <option value="support">Поддержка</option>
                <option value="voice">Голос</option>
                <option value="guide">Гайд</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Теги</label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="тег1, тег2, тег3"
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-muted-foreground mt-1">Через запятую</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Автор</label>
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="GPT Manager"
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Обложка (URL)</label>
              <input
                type="text"
                value={coverImage}
                onChange={(e) => setCoverImage(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4 space-y-4">
            <h3 className="font-semibold text-sm">SEO</h3>

            <div>
              <label className="block text-sm font-medium mb-1">Meta Title</label>
              <input
                type="text"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                placeholder="SEO заголовок"
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Meta Description</label>
              <textarea
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                rows={2}
                placeholder="SEO описание"
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
